import React, { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  SupportAgent,
  AccessTime,
  CheckCircle,
  Warning,
  CalendarMonth,
  Star,
  TrendingUp,
  ArrowBack,
  EmojiEvents,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import Chart from 'react-apexcharts'
import { api } from '../../../../services/Main/Api'

const STATUS_COLORS = ['#3B82F6', '#F59E0B', '#EF4444', '#22C55E']
const PRIORITY_COLORS = ['#94A3B8', '#3B82F6', '#F59E0B', '#EF4444']

const KpiCard = ({ title, value, subtitle, icon: Icon, color, loading }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      {loading ? (
        <>
          <Skeleton width={80} height={16} />
          <Skeleton width={60} height={36} sx={{ my: 0.5 }} />
          <Skeleton width={100} height={14} />
        </>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
              {title}
            </Typography>
            <Icon sx={{ fontSize: 20, color: color || '#94A3B8' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5, color: '#1E293B' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 12 }}>
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </CardContent>
  </Card>
)

const ChartCard = ({ title, children, loading }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1E293B' }}>
        {title}
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
      ) : (
        children
      )}
    </CardContent>
  </Card>
)

const AdminTicketDashboard = () => {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [groupBy, setGroupBy] = useState('month')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const fetchDashboard = useCallback((group = groupBy, start = startDate, end = endDate) => {
    setLoading(true)
    const payload = { group_by: group }
    if (start) payload.start_date = start
    if (end) payload.end_date = end
    api.post('tickets/dashboard/', payload)
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false))
  }, [groupBy, startDate, endDate])

  useEffect(() => {
    fetchDashboard()
  }, [])

  const handleGroupChange = (g) => {
    setGroupBy(g)
    fetchDashboard(g, startDate, endDate)
  }

  const handleDateFilter = () => {
    fetchDashboard(groupBy, startDate, endDate)
  }

  const kpis = data?.kpis || {}
  const charts = data?.charts || {}

  const commonChartOptions = {
    chart: { toolbar: { show: false }, fontFamily: 'inherit' },
    tooltip: { theme: 'light' },
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <Tooltip title="Voltar para tickets">
          <IconButton size="small" onClick={() => navigate('/admin/tickets')}>
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <SupportAgent sx={{ fontSize: 28, color: '#1E3040' }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Dashboard do Suporte
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Indicadores e métricas dos tickets
          </Typography>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="Total de Tickets"
            value={kpis.total || 0}
            subtitle={`${kpis.open || 0} abertos`}
            icon={SupportAgent}
            color="#3B82F6"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="No Mês"
            value={kpis.month_total || 0}
            subtitle="tickets criados"
            icon={CalendarMonth}
            color="#8B5CF6"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="SLA Vencido"
            value={kpis.overdue || 0}
            subtitle="tickets atrasados"
            icon={Warning}
            color={kpis.overdue > 0 ? '#EF4444' : '#22C55E'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="Tempo Médio"
            value={`${kpis.avg_resolution_hours || 0}h`}
            subtitle="para resolver"
            icon={AccessTime}
            color="#F59E0B"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="Taxa Resolução"
            value={`${kpis.resolution_rate || 0}%`}
            subtitle={`${kpis.resolved || 0} resolvidos`}
            icon={TrendingUp}
            color="#22C55E"
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <KpiCard
            title="Satisfação"
            value={kpis.avg_rating || '—'}
            subtitle={`${kpis.total_feedbacks || 0} avaliações`}
            icon={Star}
            color="#F59E0B"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Tickets por Status" loading={loading}>
            <Chart
              type="donut"
              height={280}
              options={{
                ...commonChartOptions,
                labels: charts.by_status?.labels || [],
                colors: STATUS_COLORS,
                legend: { position: 'bottom', fontSize: '13px' },
                plotOptions: { pie: { donut: { size: '55%' } } },
              }}
              series={charts.by_status?.series || []}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Abertos vs Finalizados (6 meses)" loading={loading}>
            <Chart
              type="bar"
              height={280}
              options={{
                ...commonChartOptions,
                xaxis: { categories: charts.monthly_trend?.categories || [] },
                colors: ['#3B82F6', '#22C55E'],
                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
                dataLabels: { enabled: false },
                legend: { position: 'top', fontSize: '13px' },
              }}
              series={[
                { name: 'Abertos', data: charts.monthly_trend?.opened || [] },
                { name: 'Finalizados', data: charts.monthly_trend?.resolved || [] },
              ]}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Volume Chart with Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
              Volume de Tickets
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <TextField
                type="date"
                size="small"
                label="De"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <TextField
                type="date"
                size="small"
                label="Até"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <Button variant="outlined" size="small" onClick={handleDateFilter} sx={{ color: '#475569', borderColor: '#CBD5E1' }}>
                Filtrar
              </Button>
              <ButtonGroup size="small" variant="outlined">
                {[
                  { value: 'day', label: 'Dia' },
                  { value: 'week', label: 'Semana' },
                  { value: 'month', label: 'Mês' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    onClick={() => handleGroupChange(opt.value)}
                    variant={groupBy === opt.value ? 'contained' : 'outlined'}
                    sx={{
                      ...(groupBy === opt.value
                        ? { bgcolor: '#475569', borderColor: '#475569', color: '#fff', '&:hover': { bgcolor: '#334155' } }
                        : { color: '#475569', borderColor: '#CBD5E1' }),
                    }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
          {loading ? (
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
          ) : (
            <Chart
              type="bar"
              height={300}
              options={{
                ...commonChartOptions,
                xaxis: { categories: charts.volume?.categories || [] },
                colors: ['#3B82F6', '#22C55E'],
                plotOptions: { bar: { borderRadius: 3, columnWidth: groupBy === 'day' ? '70%' : '55%' } },
                dataLabels: { enabled: groupBy !== 'day' },
                legend: { position: 'top', fontSize: '13px' },
                stroke: { show: true, width: 1, colors: ['transparent'] },
              }}
              series={[
                { name: 'Abertos', data: charts.volume?.opened || [] },
                { name: 'Finalizados', data: charts.volume?.resolved || [] },
              ]}
            />
          )}
        </CardContent>
      </Card>

      {/* Charts Row 3 */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Tickets por Categoria" loading={loading}>
            <Chart
              type="bar"
              height={280}
              options={{
                ...commonChartOptions,
                xaxis: { categories: charts.by_category?.labels || [] },
                colors: ['#6366F1'],
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '55%' } },
                dataLabels: { enabled: true },
              }}
              series={[{ name: 'Tickets', data: charts.by_category?.series || [] }]}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Tickets por Prioridade" loading={loading}>
            <Chart
              type="pie"
              height={280}
              options={{
                ...commonChartOptions,
                labels: charts.by_priority?.labels || [],
                colors: PRIORITY_COLORS,
                legend: { position: 'bottom', fontSize: '13px' },
              }}
              series={charts.by_priority?.series || []}
            />
          </ChartCard>
        </Grid>
      </Grid>

      {/* Rankings */}
      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Top Empresas — Mais Tickets" loading={loading}>
            {(data?.rankings?.top_clients || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }}>Empresa</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }} align="right">Tickets</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B', width: '30%' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.rankings?.top_clients || []).map((client, i) => {
                      const max = data.rankings.top_clients[0]?.count || 1
                      const colors = ['#F59E0B', '#94A3B8', '#CD7F32']
                      return (
                        <TableRow key={client.condo_id} hover>
                          <TableCell>
                            {i < 3 ? (
                              <EmojiEvents sx={{ fontSize: 18, color: colors[i] }} />
                            ) : (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#94A3B8', fontSize: 13 }}>{i + 1}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{client.name}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>{client.count}</Typography>
                          </TableCell>
                          <TableCell>
                            <LinearProgress
                              variant="determinate"
                              value={(client.count / max) * 100}
                              sx={{ height: 6, borderRadius: 3, bgcolor: '#F1F5F9', '& .MuiLinearProgress-bar': { bgcolor: '#3B82F6', borderRadius: 3 } }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Ranking de Satisfação — Menores Notas" loading={loading}>
            {(data?.rankings?.most_unsatisfied || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Sem dados</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }}>Empresa</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }} align="center">Nota</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: 12, color: '#64748B' }} align="right">Avaliações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.rankings?.most_unsatisfied || []).map((client, i) => {
                      const ratingIcons = {
                        5: { icon: SentimentVerySatisfied, color: '#16A34A' },
                        4: { icon: SentimentSatisfied, color: '#65A30D' },
                        3: { icon: SentimentNeutral, color: '#D97706' },
                        2: { icon: SentimentDissatisfied, color: '#EA580C' },
                        1: { icon: SentimentVeryDissatisfied, color: '#DC2626' },
                      }
                      const rounded = Math.round(client.avg_rating)
                      const cfg = ratingIcons[rounded] || ratingIcons[3]
                      const RatingIcon = cfg.icon
                      return (
                        <TableRow key={client.condo_id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#94A3B8', fontSize: 13 }}>{i + 1}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>{client.name}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`${client.avg_rating}/5`} arrow>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <RatingIcon sx={{ fontSize: 20, color: cfg.color }} />
                                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>
                                  {client.avg_rating}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Chip label={client.total_feedbacks} size="small" sx={{ fontSize: 11, height: 22, bgcolor: '#F1F5F9', fontWeight: 600 }} />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  )
}

export default AdminTicketDashboard
